import React from 'react';
import {
  DetailsCard,
  DetailsCardBody,
  DetailsCardFooter,
  ListRow,
  renderListRows,
  SkeletonText,
  useBreakpointValue,
  useThemedValueSwitch,
} from '@tq/ui-components';
import { COLOURS } from '@tq/ui-components/dist/pp2/theme';
import { useTranslation } from 'react-i18next';

import { Consultation } from 'types';
import { TitleDescription } from '../TitleDescription';

export type CaseDetailsCardProps = {
  consultation?: Consultation;
};

export const CaseDetailsCard: React.FC<CaseDetailsCardProps> = ({ consultation }) => {
  const { t } = useTranslation('case_details_card');
  const { tvs } = useThemedValueSwitch();
  const listItemColumns = useBreakpointValue({ base: '40% 60%', lg: '20% 80%' });
  if (!consultation) {
    return <SkeletonText />;
  }
  const bodyRows = [
    {
      label: t('reference', 'Reference'),
      value: consultation.reference,
    },
    {
      label: t('authority', 'Authority'),
      value: consultation.authority,
    },
    {
      label: t('address', 'Address'),
      value: consultation.address,
    },
    {
      label: t('category', 'Category'),
      value: consultation.category,
    },
    {
      label: t('consultationType', 'Consultation Type'),
      value: consultation.consultationType,
    },
  ];

  const footerRows = [
    {
      label: t('description', 'Description:'),
      value: consultation.summaryDescription,
    },
  ];

  if (consultation.consultationType !== 'RPC') {
    footerRows.push({
      label: t('reason_for_consultation', 'Reason for consultation:'),
      value: consultation.reasonForConsultation,
    });
  }

  return (
    <DetailsCard
      boxShadow='none'
      data-testid='case-details-card'
      pt={tvs({ tq: { base: '1rem' } }, { base: 0, lg: 3 })}
    >
      <DetailsCardBody pl={tvs({ tq: '1.25rem' }, 3)} pr={tvs({ tq: '1.25rem' }, 3)}>
        <ListRow
          sx={{ '& li span': { letterSpacing: '-0.2px' } }}
          listItemColumns={listItemColumns}
        >
          {renderListRows(bodyRows, {
            addLabelColons: true,
            p: tvs({ tq: 2 }, 1),
            gridGap: '1rem',
          })}
        </ListRow>
      </DetailsCardBody>
      {consultation.consultationType !== 'RPC' && (
        <DetailsCardFooter
          px={tvs({ tq: '1.25rem' }, 3)}
          py={tvs({ tq: '1.25rem' }, 3)}
          background={tvs({ tq: 'grey.50' }, COLOURS.gray[200])}
          sx={{
            '& div:not(:last-of-type) p': {
              mb: '1rem',
            },
          }}
        >
          {footerRows.map((r, i) => (
            <TitleDescription key={i} title={r.label} description={r.value} />
          ))}
        </DetailsCardFooter>
      )}
    </DetailsCard>
  );
};
